import { html, TemplateResult, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_RELAY_COMMUNICATION } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  ModuleId,
  ProxyProfile,
  SectionId,
  WhitelistedDomainItem
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  RelayCommunicationFormValue,
  RelayCommunicationSearchableKeywords,
} from '../../../../utils/uiSettings/relay/relay.types';
import {
  getRelayCommunicationSearchableKeywords,
  RELAY_COMMUNICATION_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/relay/relay.values';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { SearchableKeyword } from '../../../../../../../ui/gzSearchableAccordionsPanel/types';
import { getSupportLinkForPlatform } from '../../../../utils/links';
import {
  GzRelayCommunicationWhitelistTableWebComponent
} from '../gridComponents/GzRelayCommunicationWhitelistTableWebComponent';
import { classMap } from 'lit/directives/class-map.js';

@customElement(WEB_COMPONENT_POLICY_PANEL_RELAY_COMMUNICATION)
export class GzRelayCommunicationWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  RelayCommunicationFormValue,
  RelayCommunicationSearchableKeywords
> {
  /**
   * Path for Relay Communication section.
   */
  protected path = {
    moduleId: ModuleId.RELAY,
    sectionId: SectionId.COMMUNICATION,
  };

  /**
   * Searchable keywords for Incidents Sensor section.
   */
  protected searchableKeywords = getRelayCommunicationSearchableKeywords();

  @query('#relay-automatic-discovery-checkbox-form-field')
  private relayAutomaticDiscoveryCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  @query('#relay-cloud-services-proxy-profile-form-field')
  private relayCloudServicesProxyProfileFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('#relay-gz-proxy-profile-form-field')
  private relayGzProxyProfileFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;

  @query('gz-form-field#form-whitelisted-domains-table')
  private whitelistedDomainsTableFormField: GzFormFieldWebComponent<
    GzRelayCommunicationWhitelistTableWebComponent,
    WhitelistedDomainItem[]
  >

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`
      <gz-title
        class="content"
        searchable-label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.Relay].label}"
        ><span>${this.searchableKeywords[RelayCommunicationSearchableKeywords.Relay].translatedLabel}</span>
      </gz-title>
    `;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="relay-communication-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Define the communication settings for target endpoints with the Relay role.')}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="relay-communication-content">
        <div>
          <gz-title
            searchable-label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.Communication].label}"
            class="module-subtitle"
          >
            ${this.searchableKeywords[RelayCommunicationSearchableKeywords.Communication].translatedLabel}
          </gz-title>

          <gz-form-field class="label-mw-250 checkbox-width" id="relay-automatic-discovery-checkbox-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.AutomaticDiscovery]
                .label}"
              label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.AutomaticDiscovery]
                .translatedLabel}"
              gz-form-input
            ></gz-checkbox>
            <gz-text fontSize="12" fontWeight="400" class="helper-text mt-1"
              >${translateFn(
                'When this option is enabled in the policy, the relays will automatically perform the network discovery task every 4 hours.'
              )}
              <gz-link
                iconsize="20"
                type="underline"
                color="secondary"
                href="${getSupportLinkForPlatform(this.path, ['networkDiscovery'])}"
                fontsize="12"
              >
                <span>${translateFn('Learn more')}</span>
              </gz-link>
            </gz-text>
          </gz-form-field>

          <div class="relay-profile-container">
            ${this.renderRelayProxyProfileComponent(
              'relay-cloud-services-proxy-profile-form-field',
              this.searchableKeywords[RelayCommunicationSearchableKeywords.CommunicationBetweenRelaysAndCloudServices]
            )}
          </div>

          <div class="relay-profile-container">
            ${this.renderRelayProxyProfileComponent(
              'relay-gz-proxy-profile-form-field',
              this.searchableKeywords[RelayCommunicationSearchableKeywords.CommunicationBetweenRelaysAndGz]
            )}
          </div>

          <div class="domain-whitelist-container ${classMap({ hidden: !Application.Main.product.isCloud() })}">
            ${this.renderDomainWhitelistComponent(
              this.searchableKeywords[RelayCommunicationSearchableKeywords.WhitelistedDomains]
            )}
          </div>

        </div>
      </div>
    `;
  }

  private renderDomainWhitelistComponent(searchableKeyword: SearchableKeyword): TemplateResult {
    return html`
      <gz-title id="relay-domain-whitelist-subtitle" searchable-label="${searchableKeyword.label}" fontSize="11" fontWeight="600" class="form-subtitle"
        >${searchableKeyword.translatedLabel}
      </gz-title>

      <gz-text id="relay-domain-whitelist-info-text" fontSize="12" fontWeight="400" class="infoText"
        >${translateFn('Specify the domains you want to whitelist to allow Live Search API tasks to upload results.')}
      </gz-text>

      <gz-form-field id="form-whitelisted-domains-table">
        <gz-policy-panel-relay-communication-whitelist-table gz-form-input id="form-whitelisted-domains-table-component"></gz-policy-panel-relay-communication-whitelist-table>
      </gz-form-field>
    `;
  }

  private renderRelayProxyProfileComponent(formFieldId: string, searchableKeyword: SearchableKeyword) {
    return html`
      <gz-title searchable-label="${searchableKeyword.label}" fontSize="11" fontWeight="600" class="form-subtitle"
        >${searchableKeyword.translatedLabel}</gz-title
      >
      <gz-form-field id="${formFieldId}">
        <gz-radio-group gz-form-input class="d-flex flex-column">
          <div class="radio">
            <gz-radio
              label="${translateFn('Use previous settings')}"
              .value=${ProxyProfile.KEEP_CURRENT_SETTINGS}
            ></gz-radio>
          </div>
          <gz-text fontSize="12" fontWeight="400" class="helper-text mb-2"
            >${translateFn(
              'Select this option to preserve the proxy settings already configured via the installation package or in a previous policy.'
            )}</gz-text
          >

          <div class="radio pb-2">
            <gz-radio
              searchable-label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.UseProxy].label}"
              label=${this.searchableKeywords[RelayCommunicationSearchableKeywords.UseProxy].translatedLabel}
              .value=${ProxyProfile.ENABLE_PROXY}
            ></gz-radio>
          </div>

          <div class="radio">
            <gz-radio
              searchable-label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.DoNotUseProxy].label}"
              label="${this.searchableKeywords[RelayCommunicationSearchableKeywords.DoNotUseProxy].translatedLabel}"
              .value=${ProxyProfile.DISABLE_PROXY}
            ></gz-radio>
          </div>
        </gz-radio-group>
      </gz-form-field>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.relay.communication
   */
  protected getValueFromManager(): RelayCommunicationFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.RELAY]: { [SectionId.COMMUNICATION]: RELAY_COMMUNICATION_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.RELAY]: {
          [SectionId.COMMUNICATION]: PolicyPanelManager.getInstance().getPolicy('uiSettings.relay.communication'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    const communicationSectionFormItems  = {
      automaticDiscovery: this.relayAutomaticDiscoveryCheckboxFormField,
      cloudServicesProxy: {
        profile: this.relayCloudServicesProxyProfileFormField,
      },
      applianceProxy: {
        profile: this.relayGzProxyProfileFormField,
      },
    };

    if (Application.Main.product.isCloud()) {
      communicationSectionFormItems['whitelistedDomains'] = this.whitelistedDomainsTableFormField;
    }

    this.formGroup = FormGroup.build({
      uiSettings: {
        relay: {
          communication: communicationSectionFormItems
        },
      },
    });

  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    const communicationSectionFormValues = {
        automaticDiscovery: this.valueT.uiSettings.relay.communication.automaticDiscovery,
        cloudServicesProxy: {
          profile: this.valueT.uiSettings.relay.communication.cloudServicesProxy.profile,
        },
        applianceProxy: {
          profile: this.valueT.uiSettings.relay.communication.applianceProxy.profile,
        },
      };

    if (Application.Main.product.isCloud()) {
      communicationSectionFormValues['whitelistedDomains'] = this.valueT.uiSettings.relay.communication.whitelistedDomains;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        relay: {
          communication: communicationSectionFormValues
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'relay', 'communication'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_RELAY_COMMUNICATION: GzRelayCommunicationWebComponent;
  }
}
